// Pure builders for the flyover share wizard's config, kept out of RecordingMap.vue so the
// deterministic unit conversions (elapsed seconds → "Xh Ym", metres → km/ft via the measurement
// preference) are unit-testable in isolation.

export interface ShareConfigInput {
  id?: string | number | null;
  shareableUrl?: string | null;
  title?: string | null;
  distanceMeters?: number | null;
  elevationGainMeters?: number | null;
  elapsedSeconds?: number | null;
  thumbnailUrl?: string | null;
}

// Just the slice of MeasurementPrefUtility we use, so tests can pass a fake formatter.
export interface UnitFormatter {
  bigUnitFormatter(km: number): string;
  smallUnitFormatter(metres: number): string;
}

export interface RecordingShareConfig {
  recordingId: string;
  shareUrl: string;
  title: string;
  distance: string;
  elevation: string;
  duration: string;
  thumbnailUrl: string;
}

/** Elapsed seconds → "2h 15m" (or "15m" under an hour). Blank for missing/invalid/zero. */
export function formatFlyoverDuration(seconds?: number | null): string {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return '';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Build the flyover share wizard's config from a loaded recording, mirroring the recordings/show
 * page's ".generate-3d-flyover" data-* attributes so the wizard behaves identically from the map.
 * The measurement formatter is injected so this stays a pure function.
 */
export function buildRecordingShareConfig(
  recording: ShareConfigInput | null | undefined,
  pref: UnitFormatter,
): RecordingShareConfig {
  const r = recording || {};
  return {
    recordingId: r.id != null ? String(r.id) : '',
    shareUrl: r.shareableUrl || '',
    title: r.title || 'Recording',
    distance: r.distanceMeters != null ? pref.bigUnitFormatter(r.distanceMeters / 1000) : '',
    elevation: r.elevationGainMeters != null ? pref.smallUnitFormatter(r.elevationGainMeters) : '',
    duration: formatFlyoverDuration(r.elapsedSeconds),
    thumbnailUrl: r.thumbnailUrl || '',
  };
}
